/**
 * assets/maison-personalization.js
 *
 * Handles both:
 *   1. Drawer view switching (size ↔ personalization)
 *   2. Personalization form UI (hotspots, fonts, swatches, validation, cart)
 *
 * Depends on window.custMeta set by maison-customization-drawer.liquid.
 * Include once in theme.liquid — no other personalization JS needed.
 */

(function () {
  'use strict';

  // ════════════════════════════════════════════════════════════════════
  // PART 1 — DRAWER / VIEW SWITCHING
  // ════════════════════════════════════════════════════════════════════

  var VIEWS = { SIZE: 'size', PERSONALIZE: 'personalize' };
  var MODES = { ATC: 'atc', PERSONALIZE: 'personalize', INLINE: 'inline' };

  var TITLES = {
    size:        window.maisonI18n && window.maisonI18n.selectSize  || 'Select size',
    personalize: window.maisonI18n && window.maisonI18n.personalise || 'Personalise'
  };

  var currentView  = VIEWS.SIZE;
  var entryMode    = MODES.ATC;
  // Pre-set if a variant is already selected via URL parameter
  var sizeSelected = window.custMeta && window.custMeta.selectedVariant ? true : false;

  var drawerDialog      = null;
  var drawerHeading     = null;
  var viewSize          = null;
  var viewPersonalize   = null;
  var footerAtc         = null;
  var footerContinue    = null;
  var footerBack        = null;
  var footerAtcFinal    = null;
  var inlineDialog      = null;
  var personalizeVisited = false;

  function initDrawer() {
    drawerDialog = document.querySelector('[data-maison-variant-drawer]');
    inlineDialog = document.querySelector('[data-maison-inline-personalize-dialog]');

    if (drawerDialog) {
      drawerHeading     = drawerDialog.querySelector('[data-maison-drawer-heading]');
      viewSize          = drawerDialog.querySelector('.maison-drawer-view--size');
      viewPersonalize   = drawerDialog.querySelector('.maison-drawer-view--personalize');
      footerAtc         = drawerDialog.querySelector('[data-footer-atc]');
      footerContinue    = drawerDialog.querySelector('[data-footer-continue]');
      footerBack        = drawerDialog.querySelector('[data-footer-back]');
      footerAtcFinal    = drawerDialog.querySelector('[data-footer-atc-final]');

      bindTriggers();
      bindFooterButtons();
      bindSizeChange();
      applyPreselectedVariant();
    }

    if (inlineDialog) {
      bindInlinePersonalize();
      // Watch for inline dialog opening to init personalization UI
      var observer = new MutationObserver(function () {
        if (inlineDialog.open) {
          maybeInitPersonalization();
          swapFooterMeta(true);
        }
      });
      observer.observe(inlineDialog, { attributes: true, attributeFilter: ['open'] });
    }

    // Fallback: no drawer at all (standalone prototype)
    if (!drawerDialog && !inlineDialog) {
      maybeInitPersonalization();
    }
  }

  // ── Triggers ─────────────────────────────────────────────────────────

  function bindTriggers() {
    document.querySelectorAll('[data-maison-drawer-trigger-atc]').forEach(function (btn) {
      btn.addEventListener('click', function () { openDrawer(MODES.ATC); });
    });
    document.querySelectorAll('[data-maison-drawer-trigger-personalize]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (drawerDialog && drawerDialog.closest('dialog') && drawerDialog.closest('dialog').open) {
          // Already inside the open drawer — go straight to personalize view
          entryMode = MODES.PERSONALIZE;
          if (custHasStepA) {
            custState.position = null;
            var wrap = document.getElementById('cust-photo-wrap');
            if (wrap) wrap.querySelectorAll('.hotspot').forEach(function (h) { h.classList.remove('selected'); });
            custShowStep('a');
          }
          setView(VIEWS.PERSONALIZE);
        } else {
          // Outside the drawer — open it in personalise mode
          openDrawer(MODES.PERSONALIZE);
        }
      });
    });
    // If a variant is already selected on page load, reflect that in the footer
    if (sizeSelected) {
      if (footerContinue) footerContinue.disabled = false;
      enablePlainAtc();
      if (drawerDialog) {
        drawerDialog.querySelectorAll('[data-maison-drawer-trigger-personalize]').forEach(function (btn) {
          btn.disabled = false;
        });
      }
    }
  }

  function bindFooterButtons() {
    if (footerContinue) {
      footerContinue.addEventListener('click', function () {
        if (currentView === VIEWS.SIZE && sizeSelected) {
          // Always restart personalization from step A when entering from size view
          if (custHasStepA) {
            // Clear position so Continue can't skip step A
            custState.position = null;
            // Deselect any hotspot visually
            var wrap = document.getElementById('cust-photo-wrap');
            if (wrap) wrap.querySelectorAll('.hotspot').forEach(function (h) { h.classList.remove('selected'); });
            custShowStep('a');
          }
          setView(VIEWS.PERSONALIZE);
        }
        // Clicks when already in personalize view (step A → B) are handled
        // by the listener added in initPersonalization
      });
    }
    if (footerBack) {
      footerBack.addEventListener('click', function () {
        if (currentView === VIEWS.PERSONALIZE) {
          if (custHasStepA && custStep === 'b') {
            // Back within personalization: B → A
            custShowStep('a');
          } else {
            // Back from personalization to size view (step A or no step A)
            setView(VIEWS.SIZE);
          }
        }
      });
    }
    // Clear personalization inputs if customer uses plain ATC (no personalization)
    if (footerAtc) {
      footerAtc.addEventListener('click', clearPersonalizationInputs);
    }
  }

  function bindInlinePersonalize() {
    document.querySelectorAll('[data-maison-inline-personalize-trigger]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        entryMode = MODES.INLINE;
        var inlineDialogComponent = inlineDialog.closest('dialog-component') || inlineDialog.parentElement.closest('dialog-component');
        if (inlineDialogComponent && typeof inlineDialogComponent.showDialog === 'function') {
          inlineDialogComponent.showDialog();
        }
      });
    });
  }

  // ── Open drawer ───────────────────────────────────────────────────────

  function openDrawer(mode) {
    entryMode = mode;
    setView(VIEWS.SIZE, false);

    // Hide the in-drawer personalise button when already in personalise mode
    var inDrawerPersonalise = drawerDialog.querySelector('[data-maison-drawer-trigger-personalize]');
    if (inDrawerPersonalise) {
      inDrawerPersonalise.style.display = mode === MODES.PERSONALIZE ? 'none' : '';
    }

    var dialogComponent = drawerDialog.closest('dialog-component');
    if (dialogComponent && typeof dialogComponent.showDialog === 'function') {
      dialogComponent.showDialog();
    }
  }

  // ── View switching ────────────────────────────────────────────────────

  function setView(view, dispatch) {
    currentView = view;

    // Reconcile a URL-preselected variant before computing footer state, so the
    // plain add-to-bag isn't gated as if no size had been chosen.
    if (!sizeSelected && isVariantPreselected()) sizeSelected = true;

    if (viewSize)        viewSize.classList.toggle('maison-drawer-view--active', view === VIEWS.SIZE);
    if (viewPersonalize) viewPersonalize.classList.toggle('maison-drawer-view--active', view === VIEWS.PERSONALIZE);
    if (drawerHeading)   drawerHeading.textContent = TITLES[view];

    updateFooter();

    if (view === VIEWS.PERSONALIZE) {
      personalizeVisited = true;
      maybeInitPersonalization();
      updateFooterForCustStep();
      reapplyAtcState();
      swapFooterMeta(true);
    } else {
      // Re-assert the plain ATC enabled state every time we land on the size view.
      // The theme's add-to-cart component can re-disable the button during its own
      // hydration, which races the boot-time enable; re-running here (a user-driven
      // moment, after hydration) makes it stick.
      if (sizeSelected) {
        enablePlainAtc();
        if (entryMode === MODES.ATC && personalizeVisited) {
          setTimeout(dispatchVariantChange, 0);
        }
      }
      swapFooterMeta(false);
    }
  }

  function updateFooter() {
    var isSize        = currentView === VIEWS.SIZE;
    var isPersonalize = currentView === VIEWS.PERSONALIZE;
    var isAtcMode     = entryMode === MODES.ATC;

    show(footerAtc,         isSize && isAtcMode);
    show(footerContinue,    isSize && !isAtcMode);
    show(footerBack,        isPersonalize);
    show(footerAtcFinal,    isPersonalize);

    if (footerContinue) footerContinue.disabled = !sizeSelected;
  }

  function show(el, visible) {
    if (!el) return;
    el.style.display = visible ? '' : 'none';
  }

  function swapFooterMeta(inPersonalizeView) {
    // Drawer mode: swap price meta and terms
    var meta  = document.getElementById('maison-drawer-variant-meta');
    var terms = document.getElementById('maison-drawer-terms');
    if (meta)  meta.style.display  = inPersonalizeView ? 'none' : '';
    if (terms) terms.style.display = inPersonalizeView ? ''     : 'none';

    // Inline mode: show terms in the inline dialog footer when personalizing
    if (inlineDialog) {
      var inlineTerms = inlineDialog.querySelector('.maison-inline-terms');
      if (inlineTerms) inlineTerms.style.display = inPersonalizeView ? '' : 'none';
    }
  }

  // Updates footer when the personalization view is active,
  // accounting for internal steps (position vs text/config)
  function updateFooterForCustStep() {
    // Only applies when we're in the personalize view
    if (currentView !== VIEWS.PERSONALIZE) return;
    if (!custHasStepA) {
      // Single position — go straight to text step, show ATC
      show(footerBack,     true);
      show(footerAtcFinal, true);
      show(footerContinue, false);
    } else if (custStep === 'a') {
      // Position step — show internal Continue, hide ATC
      // Back from position step always goes to size view
      show(footerBack,     true);
      show(footerAtcFinal, false);
      show(footerContinue, true);
      if (footerContinue) footerContinue.disabled = !custState.position;
    } else {
      // Text/config step — show Back + ATC
      show(footerBack,     true);
      show(footerAtcFinal, true);
      show(footerContinue, false);
    }
  }

  function reapplyAtcState() {
    var meta = window.custMeta;
    if (!meta || !custInitialized) return;
    // On step A: ATC is always hidden (handled by updateFooterForCustStep)
    // On step B: re-run validation to set correct disabled state
    if (custStep === 'b') {
      validateCust(meta);
    } else {
      // Step A — ATC final is hidden anyway but disable it for safety
      if (footerAtcFinal) {
        footerAtcFinal.querySelectorAll('button').forEach(function (b) { b.disabled = true; });
      }
    }
  }

  // ── Size detection ────────────────────────────────────────────────────

  function bindSizeChange() {
    // Selection detected immediately — enable the controls that AREN'T gated by the
    // theme's in-progress variant change (Continue, Personalise). Do NOT enable the
    // plain ATC here: #variantChangeInProgress is still true, so an early click would
    // queue and no-op.
    document.addEventListener('change', function (e) {
      if (!e.target.closest('.maison-drawer-view--size')) return;
      if (!e.target.closest('input[type="radio"], select')) return;
      onSizeSelected();
    });

    // Variant change COMPLETED. The theme's own variant:update handler clears
    // #variantChangeInProgress AND morphs/re-renders the ATC button (the re-rendered
    // HTML may be disabled via can_add_to_cart_drawer). Defer to the next tick so our
    // enable lands AFTER that morph instead of being overwritten by it.
    document.addEventListener('variant:update', function () {
      if (!sizeSelected) return;
      setTimeout(enablePlainAtc, 0);
    });
  }

  function onSizeSelected() {
    sizeSelected = true;
    if (footerContinue) footerContinue.disabled = false;

    // Enable the in-drawer personalise button
    if (drawerDialog) {
      drawerDialog.querySelectorAll('[data-maison-drawer-trigger-personalize]').forEach(function (btn) {
        btn.disabled = false;
      });
    }
  }

  // Enables the native add-to-cart button(s) inside the [data-footer-atc] wrapper.
  // The wrapper is a <span>; the real button is the Atelier add-to-cart block
  // rendered inside it (name="add" / type="submit"), which Liquid renders disabled
  // via can_add_to_cart_drawer until a variant is selected.
  function enablePlainAtc() {
    // Query live rather than the cached reference, which can go stale after a
    // detour through the personalize view or a footer re-render.
    var el = document.querySelector('[data-maison-variant-drawer] [data-footer-atc]') || footerAtc;
    if (!el) return;
    if ('disabled' in el) el.disabled = false;
    el.querySelectorAll('button, [name="add"], [type="submit"]').forEach(function (b) {
      b.disabled = false;
    });
  }

  
  // After a detour through the personalize view, the drawer form's variant id can
  // be left unpopulated, so a plain add-to-bag submits nothing. The theme only
  // fills ref="variantId" on a real variant event — replay the checked size to
  // drive that pipeline and repopulate the form. (Also covers the still-disabled
  // case, since the same event clears the component's disabled state.)
  function nudgePlainAtcIfStuck() {
    var form = document.querySelector('[data-maison-variant-drawer] form[data-type="add-to-cart-form"]');
    var idInput = form && form.querySelector('[name="id"]');
    var atc = document.querySelector('[data-maison-variant-drawer] [data-footer-atc]');
    var btn = atc && atc.querySelector('button, [name="add"], [type="submit"]');

    var idMissing = !idInput || !idInput.value;
    var stuck     = btn && btn.disabled;

    if (idMissing || stuck) dispatchVariantChange();
  }

  // On initial load a variant may already be selected via the URL (?variant=…).
  // A pre-checked radio fires no "change" event, so onSizeSelected() never runs on
  // its own — and Liquid can render the footer ATC disabled because selected_variant
  // doesn't always resolve in the block context. Detect the preselected case and
  // treat it as a size selection so the plain add-to-bag is clickable on load.
  function applyPreselectedVariant() {
    if (!drawerDialog) return;
    if (!isVariantPreselected()) return;
    onSizeSelected();
  }

  function isVariantPreselected() {
    var hasParam = false;
    try {
      hasParam = new URLSearchParams(window.location.search).has('variant');
    } catch (e) {}
    var hasMeta = !!(window.custMeta && window.custMeta.selectedVariant);
    if (!hasParam && !hasMeta) return false;
    // Respect availability: Liquid renders the drawer trigger disabled when the
    // selected/first variant is sold out or unavailable (can_add_to_cart). Only
    // treat the variant as purchasable — and enable ATC — when it's enabled.
    var trigger = document.querySelector('[data-maison-drawer-trigger-atc]');
    if (trigger && trigger.disabled) return false;
    return true;
  }

  function hasUrlVariant() {
    try {
      return new URLSearchParams(window.location.search).has('variant');
    } catch (e) {
      return false;
    }
  }

  // The theme's add-to-cart component only clears its own `disabled` state after a
  // variant selection event. A URL-preselected variant leaves the size input
  // pre-checked but fires no such event, so the button stays disabled until the
  // shopper clicks a size. Replay that selection once, after the components have
  // hydrated, so the button enables through the theme's own path (and correctly
  // stays disabled if the preselected variant is sold out).
  // This is the fix for both inline and drawer modes.
  function replayPreselectedVariant() {
    if (!hasUrlVariant()) return;

    var done = false;
    var run = function () {
      if (done) return;
      done = true;
      // Only nudge if a button is actually stuck disabled — avoids a needless
      // section re-render on pages that already rendered the ATC enabled.
      if (!document.querySelector('add-to-cart-component button[disabled]')) return;
      dispatchVariantChange();
    };

    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('add-to-cart-component').then(function () {
        // Let connectedCallback finish wiring listeners before we dispatch.
        setTimeout(run, 0);
      });
    }
    // Fallback in case the component tag never upgrades within a reasonable window.
    setTimeout(run, 400);
  }

  function dispatchVariantChange() {
    var scopes = document.querySelectorAll('.maison-drawer-view--size, .maison-size-picker');
    scopes.forEach(function (scope) {
      var checked = scope.querySelectorAll('input[type="radio"]:checked');
      if (checked.length) {
        checked.forEach(function (input) {
          input.dispatchEvent(new Event('input',  { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
        return;
      }
      var select = scope.querySelector('select');
      if (select) {
        select.dispatchEvent(new Event('input',  { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  // ── Clear inputs on plain ATC ─────────────────────────────────────────

  function clearPersonalizationInputs() {
    [
      'h-type', 'h-input', 'h-uplift', 'h-sku-suffix', 'h-label', 'h-visible-label',
      'h-type-inline', 'h-input-inline', 'h-uplift-inline', 'h-sku-suffix-inline',
      'h-label-inline', 'h-visible-label-inline'
    ].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) el.value = '';
    });
  }

  function resetPersonalization() {
    clearPersonalizationInputs();
    custState.text = '';
    custState.color = null;
    custState.colorName = null;
    var t = document.getElementById('cust-text');
    if (t) t.value = '';
  }

  function bindCartAddReset() {
    // TODO: replace 'cart:update' with the real event name from the grep above.
    document.addEventListener('cart:update', resetPersonalization);
  }


  // ════════════════════════════════════════════════════════════════════
  // PART 2 — PERSONALIZATION FORM UI
  // ════════════════════════════════════════════════════════════════════

  var POSITION_COORDS = {
    accessory:    { x: 50, y: 30 },
    chest_left:   { x: 62, y: 32 },
    bottom_left:  { x: 62, y: 70 },
    chest_center: { x: 50, y: 32 }
  };

  var custState = {
    position:  null,
    text:      '',
    font:      null,
    color:     null,
    colorName: null
  };

  var custStep       = 'a'; // 'a' = position step, 'b' = text/config step
  var custHasStepA   = false;
  var custInitialized = false;

  function maybeInitPersonalization() {
    if (custInitialized) return;
    var meta = window.custMeta;
    if (!meta) {
      // custMeta not ready yet — retry once the current call stack clears
      console.warn('[maison-personalization] custMeta not yet set, retrying...');
      setTimeout(maybeInitPersonalization, 50);
      return;
    }
   /* console.log('[maison-personalization] initPersonalization', {
      positions: meta.positions,
      fonts:     meta.fonts,
      colors:    meta.colors,
      type:      meta.type
    });*/
    custInitialized = true;
    initPersonalization(meta);
  }

  function initPersonalization(meta) {
    // Pre-set single-option state
    if (meta.positions.length === 1) custState.position = meta.positions[0];
    if (meta.fonts.length === 1)     custState.font     = meta.fonts[0];

    // Disable the inline personalization dialog ATC until complete
    var inlineDlgEl = document.querySelector('[data-maison-inline-personalize-dialog]');
    if (inlineDlgEl) {
      inlineDlgEl.querySelectorAll('[name="add"], button[type="submit"]').forEach(function (b) {
        b.disabled = true;
      });
    }

    var hasStepA = meta.positions.length > 1;
    custHasStepA = hasStepA;
    //console.log('[maison-personalization] hasStepA:', hasStepA, '| positions:', meta.positions);

    // Hide type display on step B when step A already shows it
    var typeDisplayB = document.getElementById('cust-type-display-b');
    if (typeDisplayB) typeDisplayB.style.display = hasStepA ? 'none' : '';

    // Set initial type photo
    setCustPhoto(null);

    buildStepDots(hasStepA);

    if (hasStepA) {
      buildHotspots(meta);
      // Continue button is footerContinue in the drawer footer — already wired in bindFooterButtons
      // but it calls setView(VIEWS.PERSONALIZE) which switches the drawer view.
      // We also need custGoToB to fire to set the position badge.
      // Override footerContinue click to also call custGoToB.
      if (footerContinue) {
        footerContinue.addEventListener('click', function () {
          // Internal navigation: position step → text step
          if (currentView === VIEWS.PERSONALIZE && custStep === 'a' && custState.position) {
            custGoToB(meta);
          }
        });
      }
    }

    // Disable footerAtcFinal until personalization is complete
    if (footerAtcFinal) {
      footerAtcFinal.querySelectorAll('button').forEach(function (b) { b.disabled = true; });
    }

    if (meta.fonts.length > 1) {
      buildFonts(meta);
      var fontSection = document.getElementById('cust-font-section');
      if (fontSection) fontSection.style.display = '';
    }
    if (meta.colors.length > 0) {
      buildSwatches(meta);
      var colorSection = document.getElementById('cust-color-section');
      if (colorSection) colorSection.style.display = '';
    }

    // Hidden inputs are inside the product form in maison-buy-buttons.liquid.
    // We just need to populate them before the form submits.
    // Wire to the footer final ATC button click — before the form fires.
    if (footerAtcFinal) {
      footerAtcFinal.addEventListener('click', function () {
        populateHiddenInputs(meta);
      });
    }
    var inlineDlgEl = document.querySelector('[data-maison-inline-personalize-dialog]');
    if (inlineDlgEl) {
      var inlineAtc = inlineDlgEl.querySelector('[name="add"], button[type="submit"]');
      if (inlineAtc) {
        inlineAtc.addEventListener('click', function () { populateHiddenInputs(meta); });
      }
    }

    var textInput = document.getElementById('cust-text');
    if (textInput) textInput.addEventListener('input', function () { onText(this, meta); });

    updateCharsRemaining(0, meta);
    custShowStep(hasStepA ? 'a' : 'b');
  }

  // ── Step dots ─────────────────────────────────────────────────────────

  function buildStepDots(hasStepA) {
    var el = document.getElementById('cust-step-dots');
    if (!el) return;
    el.innerHTML = '';
    var count = hasStepA ? 2 : 1;
    for (var i = 0; i < count; i++) {
      var d = document.createElement('div');
      d.className = 'step-dot';
      d.id = 'cust-dot-' + i;
      el.appendChild(d);
    }
  }

  function custShowStep(id) {
    custStep = id;
    ['a', 'b'].forEach(function (s) {
      var el = document.getElementById('step-' + s);
      if (el) el.classList.remove('active');
    });
    var target = document.getElementById('step-' + id);
    if (target) {
      target.classList.add('active');
      //console.log('[maison-personalization] custShowStep: step-' + id + ' activated', target);
    } else {
      console.warn('[maison-personalization] custShowStep: step-' + id + ' NOT FOUND in DOM');
    }
    // Update footer buttons to reflect internal step
    updateFooterForCustStep();

    var dot0 = document.getElementById('cust-dot-0');
    var dot1 = document.getElementById('cust-dot-1');
    if (id === 'b') {
      if (dot0) dot0.classList.add('done');
    } else {
      if (dot0) dot0.classList.remove('done');
    }
    if (dot1) dot1.classList.remove('done');
  }

  function custGoToB(meta) {
    var badge = document.getElementById('cust-position-badge');
    if (badge) badge.textContent = keyToLabel(custState.position);
    custShowStep('b'); // also calls updateFooterForCustStep
    validateCust(meta); // re-gate the final ATC on text/font/color (text is empty here → disabled)
  }

  // ── Hotspots ──────────────────────────────────────────────────────────

  function buildHotspots(meta) {
    var wrap = document.getElementById('cust-photo-wrap');
    if (!wrap) return;
    meta.positions.forEach(function (key) {
      var coords = POSITION_COORDS[key] || { x: 50, y: 50 };
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'hotspot';
      btn.setAttribute('aria-label', keyToLabel(key));
      btn.dataset.key = key;
      btn.style.left = coords.x + '%';
      btn.style.top  = coords.y + '%';
      btn.innerHTML =
        '<svg viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">' +
          '<path class="plus-line" fill-rule="evenodd" d="M7.5 1a.5.5 0 0 0-1 0v5.5H1a.5.5 0 0 0 0 1h5.5V13a.5.5 0 0 0 1 0V7.5H13a.5.5 0 0 0 0-1H7.5V1z"/>' +
        '</svg>';
      btn.addEventListener('click', function () {
        wrap.querySelectorAll('.hotspot').forEach(function (h) { h.classList.remove('selected'); });
        btn.classList.add('selected');
        custState.position = key;
        // Enable the Continue button in the footer (personalise entry mode)
        onSizeSelected();
      });
      wrap.appendChild(btn);
    });
  }

  // ── Fonts ─────────────────────────────────────────────────────────────

  function buildFonts(meta) {
    var picker  = document.getElementById('cust-font-picker');
    var current = document.getElementById('cust-font-current');
    if (!picker) return;
    picker.innerHTML = '';
    meta.fonts.forEach(function (key, i) {
      var label = keyToLabel(key);
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'font-btn' + (i === 0 ? ' selected' : '');
      btn.textContent = 'Aa';
      btn.title = label;
      // btn.style.fontFamily = key; // uncomment when font files loaded
      btn.addEventListener('click', function () {
        picker.querySelectorAll('.font-btn').forEach(function (b) { b.classList.remove('selected'); });
        btn.classList.add('selected');
        custState.font = key;
        if (current) current.textContent = label;
        setCustPhoto(key);
        validateCust(meta);
      });
      picker.appendChild(btn);
    });
    custState.font = meta.fonts[0];
    if (current) current.textContent = keyToLabel(meta.fonts[0]);
    // Apply photo and badge for the pre-selected font
    setCustPhoto(meta.fonts[0]);
  }

  // ── Swatches ──────────────────────────────────────────────────────────

  function buildSwatches(meta) {
    var wrap = document.getElementById('cust-swatches');
    if (!wrap) return;
    meta.colors.forEach(function (c) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'swatch';
      btn.title = c.label;

      var dot = document.createElement('span');
      dot.className = 'swatch__dot';
      dot.style.background = c.hex;
      dot.style.borderColor = isLight(c.hex) ? '#ccc' : 'transparent';
      btn.appendChild(dot);

      btn.addEventListener('click', function () {
        wrap.querySelectorAll('.swatch').forEach(function (s) {
          s.classList.remove('selected');
          var d = s.querySelector('.swatch__dot');
          if (d) d.style.borderColor = isLight(d.style.background) ? '#ccc' : 'transparent';
        });
        btn.classList.add('selected');
        dot.style.borderColor = '#252525';
        custState.color     = c.hex;
        custState.colorName = c.label;
        var lbl = document.getElementById('cust-color-label');
        if (lbl) lbl.textContent = c.label;
        validateCust(meta);
      });

      wrap.appendChild(btn);
    });
    wrap.style.setProperty('--swatch-cols', Math.ceil(meta.colors.length / 2));
  }

  // ── Text ──────────────────────────────────────────────────────────────

  function onText(el, meta) {
    custState.text = el.value;
    updateCharsRemaining(el.value.length, meta);
    validateCust(meta);
  }

  function updateCharsRemaining(len, meta) {
    var max  = (meta && meta.maxChars) || 10;
    var left = max - len;
    var el   = document.getElementById('cust-chars-remaining');
    if (!el) return;
    var i18n = window.maisonI18n || {};
    if (i18n.charsRemaining) {
      el.textContent = i18n.charsRemaining.replace('__count__', left);
    } else {
      el.textContent = left + ' character' + (left === 1 ? '' : 's') + ' remaining';
    }
  }

  // ── Validation ────────────────────────────────────────────────────────

  function validateCust(meta) {
    var textOk  = custState.text.trim().length > 0;
    var fontOk  = meta.fonts.length <= 1  || !!custState.font;
    var colorOk = meta.colors.length === 0 || !!custState.color;
    var valid   = textOk && fontOk && colorOk;

    // Drawer mode: footer final ATC
    if (footerAtcFinal) {
      footerAtcFinal.querySelectorAll('button').forEach(function (b) { b.disabled = !valid; });
    }
    // Inline personalization dialog ATC
    var inlineDlgEl = document.querySelector('[data-maison-inline-personalize-dialog]');
    if (inlineDlgEl) {
      inlineDlgEl.querySelectorAll('[name="add"], button[type="submit"]').forEach(function (b) { b.disabled = !valid; });
    }

  }

  // ── Cart ──────────────────────────────────────────────────────────────

  function populateHiddenInputs(meta) {
    // Internal label — full detail, raw codes (for OMS / fulfillment)
    var internalParts = [meta.type.key, custState.text.toUpperCase().replace(/,/g, '&#44;')];
    if (custState.font)     internalParts.push(custState.font);
    if (custState.color)    internalParts.push(custState.color);
    if (custState.position) internalParts.push(custState.position);
    var internalLabel = internalParts.join(', ');

    // Visible label — text + only the details worth showing the customer.
    // Skip font/position when there's only one option (nothing was chosen).
    var visibleParts = [custState.text.toUpperCase()];
    if (meta.fonts.length > 1 && custState.font) visibleParts.push(keyToLabel(custState.font));
    if (custState.colorName)                     visibleParts.push(custState.colorName);
    if (meta.positions.length > 1 && custState.position) visibleParts.push(keyToLabel(custState.position));
    var visibleLabel = visibleParts.join(', ');

    var props = {
      _customization_type:       meta.type.key,
      _customization_input:      custState.text,
      _customization_uplift:     String(meta.type.uplift),
      _customization_sku_suffix: meta.type.skuSuffix,
      _customization_label:      internalLabel,
      visible_customization_label: visibleLabel
    };

    ['', '-inline'].forEach(function (suffix) {
      setHidden('h-type'         + suffix, props._customization_type);
      setHidden('h-input'        + suffix, props._customization_input);
      setHidden('h-uplift'       + suffix, props._customization_uplift);
      setHidden('h-sku-suffix'   + suffix, props._customization_sku_suffix);
      setHidden('h-label'        + suffix, props._customization_label);
      setHidden('h-visible-label' + suffix, props.visible_customization_label);
    });

    //console.log('[maison-personalization] cart properties ready to submit:', props);
  }

  function setHidden(id, value) {
    var el = document.getElementById(id);
    if (el) el.value = value;
  }

  // ── Helpers ───────────────────────────────────────────────────────────

  function getCustPhoto(type, font, color) {
  var i18n = window.maisonI18n || {};
  if (!i18n.cdnBase) return null;
  var key = type;
  if (font)  key += '_' + font;
  if (color) key += '_' + color;
  return i18n.cdnBase + 'customization_' + key + '.jpg';
}

function setCustPhoto(font) {
  var meta = window.custMeta;
  if (!meta) return;
  // Required options show the photo of THIS product's colour
  var color = (meta.required && meta.colorKey) ? meta.colorKey : null;
  var url = getCustPhoto(meta.type.key, font, color);
  if (!url) return;
  // Colour photo missing? Fall back to the generic one instead of a broken image
  var fallback = color ? getCustPhoto(meta.type.key, font) : null;
  ['cust-config-photo', 'cust-pos-photo'].forEach(function (id) {
    var img = document.getElementById(id);
    if (!img) return;
    img.onerror = fallback ? function () { img.onerror = null; img.src = fallback; } : null;
    img.src = url;
  });
  // Show font name in position badge when no position step
  if (!custHasStepA && font) {
    var badge = document.getElementById('cust-position-badge');
    if (badge) badge.textContent = keyToLabel(font);
  }
}


  function keyToLabel(key) {
    if (!key) return '';
    var i18n = window.maisonI18n || {};
    if (i18n.positions && i18n.positions[key]) return i18n.positions[key];
    return key.replace(/_/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  function isLight(hex) {
    var h = (hex || '').replace('#', '');
    if (h.length < 6) return true;
    var r = parseInt(h.substr(0, 2), 16);
    var g = parseInt(h.substr(2, 2), 16);
    var b = parseInt(h.substr(4, 2), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 195;
  }


  // ════════════════════════════════════════════════════════════════════
  // BOOT
  // ════════════════════════════════════════════════════════════════════

  function boot() {
    initDrawer();
    maybeInitPersonalization();
    replayPreselectedVariant();
    bindCartAddReset();
  }



  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();